import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Download, 
  FileJson, 
  FileSpreadsheet, 
  FileText,
  RefreshCw,
  BarChart3,
  List
} from 'lucide-react';
import { ProcessingResult, ExportFormat } from '../types';
import { exportData } from '../utils/helpers';
import StatsCards from './StatsCards';
import VehicleList from './VehicleList';

interface ResultsPanelProps {
  result: ProcessingResult | null;
  onReset: () => void;
}

type ViewMode = 'stats' | 'vehicles';

export function ResultsPanel({ result, onReset }: ResultsPanelProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('stats');
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async (format: ExportFormat) => {
    if (!result) return;
    
    setIsExporting(true);
    try {
      await exportData(format, result.vehicles, result.stats);
    } finally {
      setIsExporting(false);
    }
  };

  if (!result) {
    return (
      <div className="text-center py-12 glass rounded-2xl">
        <BarChart3 className="w-12 h-12 text-dark-600 mx-auto mb-4" />
        <p className="text-dark-400">No results to display</p>
        <p className="text-sm text-dark-500 mt-1">Process a video to see detection results</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-dark-100 font-display">
            Analysis Results
          </h2>
          <p className="text-dark-400 mt-1">
            {result.stats.total_vehicles} vehicles detected with {result.stats.total_detections} total detections
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* View toggle */}
          <div className="flex bg-dark-800 rounded-lg p-1">
            <button
              onClick={() => setViewMode('stats')}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors
                ${viewMode === 'stats' 
                  ? 'bg-primary-500 text-white' 
                  : 'text-dark-400 hover:text-dark-200'
                }
              `}
            >
              <BarChart3 className="w-4 h-4" />
              Statistics
            </button>
            <button
              onClick={() => setViewMode('vehicles')}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors
                ${viewMode === 'vehicles' 
                  ? 'bg-primary-500 text-white' 
                  : 'text-dark-400 hover:text-dark-200'
                }
              `}
            >
              <List className="w-4 h-4" />
              Vehicles
            </button>
          </div>

          {/* New analysis button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onReset}
            className="flex items-center gap-2 px-4 py-2 bg-dark-700 hover:bg-dark-600 text-dark-200 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            New Analysis
          </motion.button>
        </div>
      </div>

      {/* Content based on view mode */}
      {viewMode === 'stats' ? (
        <StatsCards stats={result.stats} />
      ) : (
        <VehicleList vehicles={result.vehicles} />
      )}

      {/* Export section */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="glass rounded-2xl p-6"
      >
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-dark-100 flex items-center gap-2">
              <Download className="w-5 h-5 text-primary-400" />
              Export Results
            </h3>
            <p className="text-sm text-dark-400 mt-1">
              Download detection data in your preferred format
            </p>
          </div>

          <div className="flex items-center gap-3">
            {[
              { format: 'json' as ExportFormat, icon: FileJson, label: 'JSON', color: 'text-yellow-400' },
              { format: 'csv' as ExportFormat, icon: FileSpreadsheet, label: 'CSV', color: 'text-green-400' },
              { format: 'pdf' as ExportFormat, icon: FileText, label: 'PDF', color: 'text-red-400' }
            ].map(({ format, icon: Icon, label, color }) => (
              <motion.button
                key={format}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleExport(format)}
                disabled={isExporting}
                className={`
                  flex items-center gap-2 px-4 py-2.5
                  bg-dark-800 hover:bg-dark-700 
                  border border-dark-600 hover:border-dark-500
                  rounded-xl transition-all
                  disabled:opacity-50 disabled:cursor-not-allowed
                `}
              >
                <Icon className={`w-5 h-5 ${color}`} />
                <span className="text-dark-200 font-medium">{label}</span>
              </motion.button>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Speed distribution chart (simplified) */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="glass rounded-2xl p-6"
      >
        <h3 className="text-lg font-semibold text-dark-100 mb-4">
          Speed Distribution
        </h3>
        
        <div className="space-y-3">
          {[
            { range: '0-30 km/h', color: 'from-green-500 to-green-600' },
            { range: '30-60 km/h', color: 'from-yellow-500 to-amber-500' },
            { range: '60-90 km/h', color: 'from-orange-500 to-orange-600' },
            { range: '90+ km/h', color: 'from-red-500 to-red-600' }
          ].map((bucket, i) => {
            const vehiclesInRange = result.vehicles.filter(v => {
              const speed = v.max_speed;
              switch (i) {
                case 0: return speed < 30;
                case 1: return speed >= 30 && speed < 60;
                case 2: return speed >= 60 && speed < 90;
                case 3: return speed >= 90;
                default: return false;
              }
            }).length;
            
            const percentage = result.vehicles.length > 0 
              ? (vehiclesInRange / result.vehicles.length) * 100 
              : 0;

            return (
              <div key={bucket.range} className="flex items-center gap-4">
                <span className="text-sm text-dark-400 w-24">{bucket.range}</span>
                <div className="flex-grow h-6 bg-dark-800 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${percentage}%` }}
                    transition={{ delay: 0.5 + i * 0.1, duration: 0.5 }}
                    className={`h-full bg-gradient-to-r ${bucket.color} rounded-full`}
                  />
                </div>
                <span className="text-sm font-mono text-dark-300 w-20 text-right">
                  {vehiclesInRange} ({percentage.toFixed(0)}%)
                </span>
              </div>
            );
          })}
        </div>
      </motion.div>
    </motion.div>
  );
}

export default ResultsPanel;
